import { type ReactNode } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { Tier } from '../hooks/use-pricing-table';
import { PricingTierList } from './pricing-tier-list';

type Props = { tiers: Tier[]; onChange: (tiers: Tier[]) => void };

function tierIds(tiers: Tier[]): string[] {
  return tiers.map((_, i) => `tier-${i}`);
}

function addTier(tiers: Tier[]): Tier[] {
  const last = tiers[tiers.length - 1];
  if (!last) return [{ min: 1, max: null, price_per_lead: 0.15 }];
  const newMax = last.min + 49;
  const ins: Tier = {
    min: last.min,
    max: newMax,
    price_per_lead: last.price_per_lead,
  };
  return [...tiers.slice(0, -1), ins, { ...last, min: newMax + 1 }];
}

function Header(): ReactNode {
  return (
    <div className="flex items-center gap-sm px-1 pb-1">
      <span className="w-[14px]" />
      <span className="w-[140px] text-[10px] font-medium uppercase text-text-tertiary/60">
        Rango creadores/día
      </span>
      <span className="text-[10px] font-medium uppercase text-text-tertiary/60">
        $ / creador
      </span>
    </div>
  );
}

function tierErrors(tiers: Tier[]): { range: Set<number>; price: Set<number> } {
  const range = new Set<number>();
  const price = new Set<number>();
  for (let i = 0; i < tiers.length; i++) {
    const t = tiers[i];
    if (!t) continue;
    if (t.price_per_lead <= 0) price.add(i);
    if (i === 0 && t.min !== 1) range.add(i);
    const prev = tiers[i - 1];
    if (prev && t.min !== (prev.max ?? 0) + 1) range.add(i);
  }
  return { range, price };
}

export function PricingTierEditor({ tiers, onChange }: Props): ReactNode {
  const { range, price } = tierErrors(tiers);
  return (
    <div>
      <Header />
      <PricingTierList
        tiers={tiers}
        ids={tierIds(tiers)}
        errors={range}
        priceErrors={price}
        onChange={onChange}
      />
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onChange(addTier(tiers))}
        className="mt-sm gap-1"
      >
        <Plus size={14} /> Añadir tramo
      </Button>
    </div>
  );
}
